import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";
import { extractErrorMessage } from "utils/errorHandler";

export interface Leave {
  fromDate: string;
  toDate: string;
  reason: string;
  leaveType: string;
  convertToUnpaid: boolean;
}

export interface MultipleLeavePayload {
  departmentId: string;
  leaves: Leave[];
}

export interface MultipleLeaveResponse {
  success: boolean;
  message: string;
  data: {
    departmentId: string;
    leaves: {
      _id: string;
      fromDate: string;
      toDate: string;
      reason: string;
      leaveType: string;
      convertToUnpaid: boolean;
    }[];
  };
}

const addMultipleLeaves = async (
  payload: MultipleLeavePayload
): Promise<MultipleLeaveResponse> => {
  const { data } = await customAxios.post<MultipleLeaveResponse>(
    "/leaves/multiple-leave",
    payload
  );
  return data;
};

export const useAddMultipleLeaves = () => {
  const queryClient = useQueryClient();

  return useMutation<MultipleLeaveResponse, AxiosError, MultipleLeavePayload>({
    mutationFn: addMultipleLeaves,
    onSuccess: async (data) => {
      showNotification({
        title: "Success",
        message: data.message || "Leaves added successfully",
        color: "green",
      });

      await queryClient.invalidateQueries(["leaves"]);
    },
    onError: (error) => {
      showNotification({
        title: "Multiple Leaves Failed",
        message: extractErrorMessage(error, "Failed to add multiple leaves"),
        color: "red",
        autoClose: 8000, // Show for 8 seconds for longer messages
      });
    },
  });
};
